import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError, apiFetch, apiFetchJson } from '../../lib/apiFetch.js';
import type { SettingsPatchInput, SettingsResponse } from './contractsMirror.js';

/**
 * TanStack Query hooks for the coarse company-settings endpoint (the strangler-fig +
 * coarse-endpoint proof, CARVE-07). The SERVER owns the tenant config (22-07); the SPA
 * renders the returned `SettingsResponse` and PATCHes the mutable subset.
 *
 * Both calls go through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived
 * server-side from the auth context; the SPA NEVER sends a tenant id param (threat
 * T-22-tenant). The PATCH rides apiFetch's double-submit CSRF on the non-safe method.
 */

/** The stable query key for the workspace settings (one cache entry). */
export function settingsQueryKey(): readonly unknown[] {
  return ['settings'] as const;
}

export function useSettingsQuery(options: { enabled?: boolean; staleTime?: number } = {}) {
  return useQuery<SettingsResponse>({
    queryKey: settingsQueryKey(),
    queryFn: () => apiFetchJson<SettingsResponse>('/api/v1/settings'),
    enabled: options.enabled ?? true,
    staleTime: options.staleTime,
  });
}

export function useSettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: boolean }, Error, SettingsPatchInput>({
    mutationFn: (input) =>
      // NO tenantId — the server resolves the tenant from the principal (SEC-11).
      apiFetchJson<{ ok: boolean }>('/api/v1/settings', {
        method: 'PATCH',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsQueryKey() });
    },
  });
}

/**
 * Save company policy settings alone (`PATCH /api/v1/settings { companySettings }`, Admin only on
 * the server). The cached response is patched in place instead of refetched: a refetch hands the
 * main settings form a new `settings` object, which resets anything typed there and not saved.
 */
export function useCompanySettingsMutation() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: boolean }, Error, Record<string, unknown>>({
    mutationFn: (companySettings) =>
      apiFetchJson<{ ok: boolean }>('/api/v1/settings', {
        method: 'PATCH',
        body: JSON.stringify({ companySettings }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: (_data, patch) => {
      queryClient.setQueryData<SettingsResponse>(settingsQueryKey(), (old) =>
        old ? { ...old, companySettings: { ...old.companySettings, ...patch } } : old,
      );
    },
  });
}

/**
 * Upload (or replace) the company logo (`POST /api/v1/settings/logo`, 22-21 —
 * settings.manage, server-gated). The body is a `FormData` whose file field is named
 * `logo`; the SPA never sends a tenant id (SEC-11). On success the settings query is
 * invalidated so `logoPath` refreshes and the preview re-fetches. Rides raw `apiFetch`
 * (double-submit CSRF on the non-safe method) so the multipart body passes untouched.
 */
export function useUploadLogoMutation() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: boolean }, Error, FormData>({
    mutationFn: async (formData) => {
      const response = await apiFetch('/api/v1/settings/logo', {
        method: 'POST',
        body: formData,
      });
      const body = (await response.json().catch(() => null)) as { ok?: boolean } | null;
      if (!response.ok || !body || body.ok === false) {
        throw new ApiError(response.status, 'logo_upload_failed');
      }
      return { ok: true };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsQueryKey() });
    },
  });
}

export function useJoinCodeMutation() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: boolean }, Error, { enabled: boolean }>({
    mutationFn: (input) =>
      apiFetchJson<{ ok: boolean }>('/api/v1/settings/join-code', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsQueryKey() });
    },
  });
}

/**
 * Start Stripe Connect onboarding (`POST /api/v1/settings/stripe/connect`, 21-12 —
 * settings.manage). Returns the Stripe onboarding URL for the caller to navigate to; the endpoint
 * never issues an HTTP redirect. NO tenantId — the server resolves the principal (SEC-11). A
 * non-ok envelope (e.g. stripe_connect_failed) surfaces as `isError` via apiFetchJson's throw.
 */
export function useStripeConnectMutation() {
  return useMutation<{ ok: boolean; onboardingUrl?: string }, Error, void>({
    mutationFn: () =>
      apiFetchJson<{ ok: boolean; onboardingUrl?: string }>('/api/v1/settings/stripe/connect', {
        method: 'POST',
      }),
  });
}

/**
 * Finalize Stripe onboarding on the `?stripe=return` redirect (`POST /api/v1/settings/stripe/return`,
 * 21-12 — settings.view). The server persists onboarded_at only when charges_enabled; on success we
 * invalidate the settings query so the card flips to connected.
 */
export function useStripeReturnMutation() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: boolean; stripeConnected?: boolean }, Error, void>({
    mutationFn: () =>
      apiFetchJson<{ ok: boolean; stripeConnected?: boolean }>('/api/v1/settings/stripe/return', {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsQueryKey() });
    },
  });
}

/**
 * Disconnect the tenant's Stripe Connect account (`POST /api/v1/settings/stripe/disconnect`, 21-12
 * — settings.manage). On success the settings query is invalidated so the card flips to
 * not-connected.
 */
export function useStripeDisconnectMutation() {
  const queryClient = useQueryClient();

  return useMutation<{ ok: boolean; stripeConnected?: boolean }, Error, void>({
    mutationFn: () =>
      apiFetchJson<{ ok: boolean; stripeConnected?: boolean }>(
        '/api/v1/settings/stripe/disconnect',
        { method: 'POST' },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: settingsQueryKey() });
    },
  });
}
